import React, { useEffect, useMemo, useState } from 'react';
import { Check, CircleOff, Code2, Filter, Plus, RotateCcw, Save, Settings2, ShieldCheck, Trash2, X } from 'lucide-react';
import { DEFAULT_POINT_METRICS, MetricCategory, MetricFilterRule, PointMetricDefinition } from '../data/pointSystemConfig';
import { FILTER_OPERATORS, FilterScope, getRelatedRecordSchemas, getSourceObjectSchema, RelatedRecordSchema, SELECTABLE_SOURCE_OBJECT_SCHEMAS, SourceObjectId, SourceObjectSchema } from '../data/querySchema';
interface PointSystemConfigPanelProps {
  metrics: PointMetricDefinition[];
  onClose: () => void;
  onSave: (metrics: PointMetricDefinition[]) => void;
}
const CATEGORIES: MetricCategory[] = ['Core Production', 'Speed & Quality Bonuses', 'Response Time', 'Dial Activity', 'Loan Partner Requirements', 'Quality Control & Penalties'];
export function PointSystemConfigPanel({
  metrics,
  onClose,
  onSave
}: PointSystemConfigPanelProps) {
  const [draft, setDraft] = useState<PointMetricDefinition[]>(metrics);
  const [selectedId, setSelectedId] = useState(metrics[0]?.id ?? '');
  const [category, setCategory] = useState<MetricCategory>('Core Production');
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    setDraft(metrics);
    setCategory(currentCategory => metrics.some(metric => metric.category === currentCategory) ? currentCategory : 'Core Production');
    setSelectedId(currentId => metrics.some(metric => metric.id === currentId) ? currentId : metrics[0]?.id ?? '');
  }, [metrics]);
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);
  const categoryMetrics = useMemo(() => draft.filter(metric => metric.category === category), [category, draft]);
  const selectedMetric = draft.find(metric => metric.id === selectedId) ?? categoryMetrics[0];
  const selectCategory = (nextCategory: MetricCategory) => {
    setCategory(nextCategory);
    setSelectedId(draft.find(metric => metric.category === nextCategory)?.id ?? '');
  };
  const updateMetric = (id: string, changes: Partial<PointMetricDefinition>) => {
    setDraft(current => current.map(metric => metric.id === id ? {
      ...metric,
      ...changes
    } : metric));
    setSaved(false);
  };
  const handleSave = () => {
    onSave(draft);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1800);
  };
  const handleReset = () => {
    const resetMetrics = DEFAULT_POINT_METRICS.map(metric => ({
      ...metric,
      conditions: metric.conditions.map(rule => ({
        ...rule
      }))
    }));
    setDraft(resetMetrics);
    setCategory('Core Production');
    setSelectedId(resetMetrics[0]?.id ?? '');
    setSaved(false);
  };
  return <section className="flex min-h-[720px] w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-gray-50 shadow-sm" aria-label="Point system admin configuration" data-id="element-669" data-mp="3gvx7qll8d7oq">
      <header className="flex shrink-0 items-center justify-between gap-4 bg-[#0a1e5e] px-5 py-4 text-white sm:px-6" data-id="element-670" data-mp="3qnb1ecqi3d9y">
        <div className="flex min-w-0 items-center gap-3" data-id="element-671" data-mp="3lp9j3u0wfhzj">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/15" data-id="element-672" data-mp="2xy441h2s8tqa">
            <Settings2 className="h-5 w-5" data-id="element-673" data-mp="2szj3thnj6a1u" />
          </div>
          <div className="min-w-0" data-id="element-674" data-mp="2t13wt2u75kxl">
            <p className="text-xs font-semibold uppercase tracking-wider text-blue-200" data-id="element-675" data-mp="36yskeifnf66c">
              Admin workspace
            </p>
            <h2 className="truncate text-lg font-extrabold" data-id="element-676" data-mp="32wnrjlx0ydkr">
              Point System Configuration
            </h2>
          </div>
        </div>
        <button type="button" onClick={onClose} className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-sm font-bold transition-colors hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white" aria-label="Return to Point System reference" data-id="element-677" data-mp="manwzm5yqn7a">
          <X className="h-4 w-4" data-id="element-678" data-mp="1mhjn4x9f937h" />
          <span className="hidden sm:inline" data-id="element-679" data-mp="1mhb4spuj55ow">Return to reference</span>
        </button>
      </header>

      <div className="flex shrink-0 items-start gap-3 border-b border-blue-100 bg-blue-50 px-5 py-3 text-sm text-blue-900 sm:px-6" data-id="element-680" data-mp="1riquwwbke2e5">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" data-id="element-681" data-mp="1wefuv0rhxlaz" />
        <p data-id="element-682" data-mp="20g15efgk5ni0">
          Configure each metric’s source, attribution, exclusions, and optional custom query using the expanded workspace.
        </p>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden xl:grid xl:grid-cols-[280px_320px_minmax(0,1fr)]" data-id="element-683" data-mp="2a7x49sqgxhna">
        <nav className="shrink-0 overflow-x-auto border-b border-gray-200 bg-white p-3 xl:overflow-y-auto xl:border-b-0 xl:border-r" data-id="element-684" data-mp="2f7jju8trr9z9">
          <p className="mb-2 px-2 text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-685" data-mp="2k635s98d4lrm">
            Metric groups
          </p>
          <div className="flex gap-1 xl:flex-col" data-id="element-686" data-mp="3joekbpotrtb">
            {CATEGORIES.map(item => {
            const active = item === category;
            const count = draft.filter(metric => metric.category === item).length;
            return <button key={item} type="button" onClick={() => selectCategory(item)} className={`flex min-w-max items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-bold transition-colors xl:min-w-0 ${active ? 'bg-[#0a1e5e] text-white' : 'text-gray-600 hover:bg-gray-50 hover:text-[#0a1e5e]'}`} data-id="element-687" data-mp="3pszfseofym1z">
                  <span data-id="element-688" data-mp="19z76dcxgqm7a">{item}</span>
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${active ? 'bg-white/15 text-blue-100' : 'bg-gray-100 text-gray-500'}`} data-id="element-689" data-mp="1a08twgvo115w">
                    {count}
                  </span>
                </button>;
          })}
          </div>
        </nav>

        <section className="min-h-0 overflow-y-auto border-b border-gray-200 bg-white p-3 xl:border-b-0 xl:border-r" data-id="element-690" data-mp="34ew3myhesoy4">
          <p className="mb-2 px-2 text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-691" data-mp="2poymh2hwczif">
            {category}
          </p>
          <div className="space-y-1" data-id="element-692" data-mp="2gplbg5e4k2vl">
            {categoryMetrics.map(metric => {
            const active = selectedMetric?.id === metric.id;
            return <button key={metric.id} type="button" onClick={() => setSelectedId(metric.id)} className={`w-full rounded-lg border p-3 text-left transition-colors ${active ? 'border-blue-300 bg-blue-50 shadow-sm' : 'border-transparent hover:border-gray-200 hover:bg-gray-50'}`} data-id="element-693" data-mp="26rx50i75p6xl">
                  <div className="flex items-start justify-between gap-2" data-id="element-694" data-mp="9yvl900xovz2">
                    <p className="text-sm font-bold text-gray-800" data-id="element-695" data-mp="1uih8yunp7xsx">
                      {metric.label}
                    </p>
                    <span className={`shrink-0 text-sm font-extrabold ${metric.points > 0 ? 'text-green-600' : metric.points < 0 ? 'text-red-600' : 'text-gray-400'}`} data-id="element-696" data-mp="1pgi41gcb8stv">
                      {metric.points > 0 ? '+' : ''}
                      {metric.points.toLocaleString()}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-2" data-id="element-697" data-mp="288awn9ngwc12">
                    <span className={`h-2 w-2 rounded-full ${metric.enabled ? 'bg-green-500' : 'bg-gray-300'}`} data-id="element-698" data-mp="2n63e47ckrahw" />
                    <span className="text-[11px] font-semibold text-gray-500" data-id="element-699" data-mp="2i81qra848p0m">
                      {metric.enabled ? 'Active' : 'Disabled'} ·{' '}
                      {metric.conditions.length} condition
                      {metric.conditions.length === 1 ? '' : 's'}
                    </span>
                  </div>
                </button>;
          })}
          </div>
        </section>

        <section className="min-h-0 overflow-y-auto p-5 sm:p-6 xl:p-8" data-id="element-700" data-mp="6x3f975vx0y8">
          {selectedMetric ? <MetricEditor metric={selectedMetric} onUpdate={changes => updateMetric(selectedMetric.id, changes)} data-id="element-701" data-mp="bs90s4vdkdls" /> : <div className="py-16 text-center text-sm font-semibold text-gray-500" data-id="element-702" data-mp="3f86f8q0kk7px">
              Select a metric to configure it.
            </div>}
        </section>
      </div>

      <footer className="flex shrink-0 flex-col-reverse gap-3 border-t border-gray-200 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6" data-id="element-703" data-mp="3p3zzywofz7is">
        <button type="button" onClick={handleReset} className="inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-bold text-gray-600 transition-colors hover:bg-gray-100 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-704" data-mp="14f3jaw8mt2wm">
          <RotateCcw className="h-4 w-4" data-id="element-705" data-mp="2owcp2dpn1udf" />
          Restore defaults
        </button>
        <div className="flex items-center gap-3" data-id="element-706" data-mp="2k5miix5h7lh5">
          {saved && <span className="inline-flex items-center gap-1.5 text-sm font-bold text-green-600" data-id="element-707" data-mp="2k3jn1xlk1051">
              <Check className="h-4 w-4" data-id="element-708" data-mp="2f4yumkcjnkr5" /> Saved
            </span>}
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-709" data-mp="2a9016icczcjv">
            Cancel
          </button>
          <button type="button" onClick={handleSave} className="inline-flex items-center gap-2 rounded-lg bg-[#0a1e5e] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-710" data-mp="1wa0evhcbj9qv">
            <Save className="h-4 w-4" data-id="element-711" data-mp="2w13opu26ddjh" />
            Save changes
          </button>
        </div>
      </footer>
    </section>;
}
function MetricEditor({
  metric,
  onUpdate
}: {
  metric: PointMetricDefinition;
  onUpdate: (changes: Partial<PointMetricDefinition>) => void;
}) {
  const schema = getSourceObjectSchema(metric.sourceObject);
  const relatedRecords = getRelatedRecordSchemas(schema);
  const dateFields = schema.sourceFields.filter(field => field.valueType === 'date');
  const changeSourceObject = (sourceObject: SourceObjectId) => {
    const nextSchema = getSourceObjectSchema(sourceObject);
    onUpdate({
      sourceObject,
      sourceUserField: nextSchema.userFields[0].key,
      timeframeField: nextSchema.sourceFields.find(field => field.valueType === 'date')?.key ?? '',
      conditions: []
    });
  };
  const addCondition = () => {
    const field = schema.sourceFields[0];
    if (!field) return;
    onUpdate({
      conditions: [...metric.conditions, {
        id: `${metric.id}-condition-${Date.now()}`,
        scope: 'source' as const,
        field: field.key,
        operator: 'equals' as const,
        value: ''
      }]
    });
  };
  const updateCondition = (index: number, changes: Partial<MetricFilterRule>) => {
    onUpdate({
      conditions: metric.conditions.map((rule, ruleIndex) => ruleIndex === index ? {
        ...rule,
        ...changes
      } : rule)
    });
  };
  const removeCondition = (index: number) => {
    onUpdate({
      conditions: metric.conditions.filter((_, ruleIndex) => ruleIndex !== index)
    });
  };
  const timeframeLabel = dateFields.find(field => field.key === metric.timeframeField)?.label ?? metric.timeframeField;
  return <div className="mx-auto max-w-2xl" data-id="element-712" data-mp="2ylruddw80afc">
      <div className="mb-6 flex items-start justify-between gap-4" data-id="element-713" data-mp="33is0tpwzff6z">
        <div data-id="element-714" data-mp="2jgdyadhak9co">
          <p className="text-[10px] font-bold uppercase tracking-wider text-blue-600" data-id="element-715" data-mp="2jgx816wsfrfm">
            Metric query definition
          </p>
          <h3 className="mt-1 text-xl font-extrabold text-[#0a1e5e]" data-id="element-716" data-mp="2ffbztyv781br">
            {metric.label}
          </h3>
        </div>
        <button type="button" role="switch" aria-checked={metric.enabled} onClick={() => onUpdate({
        enabled: !metric.enabled
      })} className={`inline-flex shrink-0 items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${metric.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-717" data-mp="2alzafmip3qhy">
          <span className={`h-2.5 w-2.5 rounded-full ${metric.enabled ? 'bg-green-500' : 'bg-gray-400'}`} data-id="element-718" data-mp="1rphoxmufwmu5" />
          {metric.enabled ? 'Active' : 'Disabled'}
        </button>
      </div>

      <div className="space-y-5" data-id="element-719" data-mp="3m1651sceqvg4">
        <div className="rounded-xl border border-gray-200 bg-slate-950 p-4 text-white shadow-sm" data-id="element-720" data-mp="3h4fhkjy9wxko">
          <div className="mb-4 flex items-center gap-2" data-id="element-721" data-mp="3onyiw6bdxdi2">
            <Settings2 className="h-4 w-4 text-blue-300" data-id="element-722" data-mp="3onf06ndwul2z" />
            <div data-id="element-723" data-mp="3tm01kq4wfat0">
              <h4 className="text-sm font-extrabold" data-id="element-724" data-mp="3tlgopnu3vclu">Metric query</h4>
              <p className="text-xs text-slate-300" data-id="element-725" data-mp="27kj6u78846z">
                Define the record set and attribution used by this scorecard metric.
              </p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2" data-id="element-726" data-mp="35p2puyb9j55t">
            <label className="block" data-id="element-727" data-mp="35qng4bqjo1xv">
              <span className="mb-1.5 block text-xs font-bold text-blue-200" data-id="element-728" data-mp="3annmknrb36pi">
                Source Object
              </span>
              <select value={metric.sourceObject} onChange={event => changeSourceObject(event.target.value as SourceObjectId)} className="w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm font-semibold text-white focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-729" data-mp="3flxij0kuubc6">
                {SELECTABLE_SOURCE_OBJECT_SCHEMAS.map(source => <option key={source.id} value={source.id} data-id="element-730" data-mp="siwae2441p7v">
                    {source.label}
                  </option>)}
              </select>
              <p className="mt-1.5 text-[11px] leading-snug text-slate-400" data-id="element-731" data-mp="17d0utqokdj58">
                {schema.description}
              </p>
            </label>
            <label className="block" data-id="element-732" data-mp="1gf0dsoj52xat">
              <span className="mb-1.5 block text-xs font-bold text-blue-200" data-id="element-733" data-mp="1gdyyu8z05qnt">
                Source User
              </span>
              <select value={metric.sourceUserField} onChange={event => onUpdate({
              sourceUserField: event.target.value
            })} className="w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm font-semibold text-white focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-734" data-mp="1q5lhurhl0u14">
                {schema.userFields.map(field => <option key={field.key} value={field.key} data-id="element-735" data-mp="2dr5n64ayufaa">
                    {field.label}
                  </option>)}
              </select>
              <p className="mt-1.5 text-[11px] leading-snug text-slate-400" data-id="element-736" data-mp="24rildhebr7gp">
                Identifies the user who receives the points from this record.
              </p>
            </label>
            <label className="block sm:col-span-2" data-id="element-737" data-mp="1v2ihbjkytlvn">
              <span className="mb-1.5 block text-xs font-bold text-blue-200" data-id="element-738" data-mp="1v0xopzi7od0f">
                Timeframe Date Field
              </span>
              <select value={metric.timeframeField} onChange={event => onUpdate({
              timeframeField: event.target.value
            })} disabled={dateFields.length === 0} className="w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm font-semibold text-white focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60" data-id="element-739" data-mp="1m2lzo1wztna3">
                {dateFields.length === 0 ? <option value="" data-id="element-740" data-mp="3e0n7b4bohw3w">No date field on this object</option> : dateFields.map(field => <option key={field.key} value={field.key} data-id="element-741" data-mp="39xh64b3fav4a">
                      {field.label}
                    </option>)}
              </select>
              <p className="mt-1.5 text-[11px] leading-snug text-slate-400" data-id="element-742" data-mp="5jiwdg9skp8e">
                The date the scorecard timeframe is applied to, e.g. Credit Pull
                Date between the selected period.
              </p>
            </label>
          </div>
          <div className={`mt-4 rounded-lg border px-3 py-2.5 text-xs ${metric.customQueryEnabled ? 'border-amber-400/40 bg-amber-400/10 text-amber-100' : 'border-slate-700 bg-slate-900 text-slate-300'}`} data-id="element-743" data-mp="3nq6s4fiyc01e">
            {metric.customQueryEnabled ? <span className="inline-flex items-center gap-1.5 font-bold text-amber-200" data-id="element-744" data-mp="228o67l101bro">
                <Code2 className="h-3.5 w-3.5" data-id="element-745" data-mp="276zfnundtk9d" />
                Custom query override is the effective definition for this metric.
              </span> : <>
                <span className="font-bold text-blue-200" data-id="element-746" data-mp="2gx289tqlcw76">Query preview: </span>
                <span className="font-mono text-[11px] leading-relaxed text-slate-200" data-id="element-747" data-mp="2gz5815hjq3p3">
                  COUNT {schema.label} BY{' '}
                  {schema.userFields.find(field => field.key === metric.sourceUserField)?.label ?? metric.sourceUserField}{' '}
                  WHERE {timeframeLabel || 'Date'} = (timeframe)
                  {metric.conditions.map(rule => {
                const relatedRecord = relatedRecords.find(record => record.key === rule.relatedRecord);
                const fields = rule.scope === 'related' ? relatedRecord?.fields ?? [] : schema.sourceFields;
                const fieldLabel = fields.find(item => item.key === rule.field)?.label ?? rule.field;
                const operatorLabel = FILTER_OPERATORS.find(operator => operator.value === rule.operator)?.label ?? rule.operator;
                const prefix = rule.scope === 'related' && relatedRecord ? `${relatedRecord.label}.` : '';
                const needsValue = rule.operator !== 'is_true' && rule.operator !== 'is_false';
                return <React.Fragment key={rule.id} data-id="element-748" data-mp="cpzem99zq9rf">
                        {` AND ${prefix}${fieldLabel} ${operatorLabel.toLowerCase()}`}
                        {needsValue ? ` "${rule.value || '…'}"` : ''}
                      </React.Fragment>;
              })}
                </span>
              </>}
          </div>
        </div>

        <label className="block" data-id="element-749" data-mp="6b0k1an1m6vt">
          <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-600" data-id="element-750" data-mp="6bttwkhatci6">
            Metric name
          </span>
          <input value={metric.label} onChange={event => onUpdate({
          label: event.target.value
        })} className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm font-semibold text-gray-800 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-751" data-mp="bby2lobyt2xu" />
        </label>

        <div className="grid gap-5 sm:grid-cols-[minmax(0,1fr)_160px]" data-id="element-752" data-mp="u7xvr2goicj0">
          <label className="block" data-id="element-753" data-mp="1hyae779jvrnu">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-600" data-id="element-754" data-mp="1hx900tx8mp1w">
              Main criteria
            </span>
            <textarea value={metric.criteria} onChange={event => onUpdate({
            criteria: event.target.value
          })} rows={4} className="w-full resize-y rounded-lg border border-gray-200 px-3 py-2.5 text-sm leading-relaxed text-gray-700 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-755" data-mp="1mqv43xpc5km3" />
          </label>
          <label className="block" data-id="element-756" data-mp="39hbmr6tyy5pj">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-600" data-id="element-757" data-mp="2vj3qz28o5afy">
              Points
            </span>
            <input type="number" min={-5000} max={5000} value={metric.points} onChange={event => onUpdate({
            points: Number(event.target.value) || 0
          })} className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-lg font-extrabold text-[#0a1e5e] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-758" data-mp="2zmrwzghnjxz3" />
            <p className="mt-2 text-xs leading-snug text-gray-500" data-id="element-759" data-mp="29aeuc2zubofy">
              Use a negative value for a deduction. Set to 0 to track without
              awarding points.
            </p>
          </label>
        </div>

        <FilterGroup timeframeLabel={timeframeLabel} emptyState={`No conditions set. Every ${schema.label} record in the timeframe will count.`} rules={metric.conditions} schema={schema} relatedRecords={relatedRecords} onAdd={addCondition} onUpdate={updateCondition} onRemove={removeCondition} data-id="element-760" data-mp="2j1szaqak9ggh" />

        <div className={`rounded-xl border p-4 ${metric.customQueryEnabled ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white'}`} data-id="element-761" data-mp="31yu5gefupmm0">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-762" data-mp="2wyplufjncbrk">
            <div className="flex items-start gap-2.5" data-id="element-763" data-mp="17nyn4kev6t61">
              <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${metric.customQueryEnabled ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-764" data-mp="12nj7fm7419kw">
                <Code2 className="h-4 w-4" data-id="element-765" data-mp="1wbqj6dc7sr6k" />
              </div>
              <div data-id="element-766" data-mp="1webk904ov339">
                <h4 className="text-sm font-extrabold text-gray-800" data-id="element-767" data-mp="21b229t0208rb">
                  Custom query override
                </h4>
                <p className="mt-0.5 text-xs leading-snug text-gray-500" data-id="element-768" data-mp="26bhegh8lna7p">
                  For advanced definitions, replace the visual source, attribution, and condition builder with a custom query.
                </p>
              </div>
            </div>
            <button type="button" role="switch" aria-checked={metric.customQueryEnabled} onClick={() => onUpdate({
            customQueryEnabled: !metric.customQueryEnabled
          })} className={`inline-flex shrink-0 items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${metric.customQueryEnabled ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-769" data-mp="2k2cpo06km3eg">
              <span className={`h-2.5 w-2.5 rounded-full ${metric.customQueryEnabled ? 'bg-white' : 'bg-gray-400'}`} data-id="element-770" data-mp="id6sx58hwtc3" />
              {metric.customQueryEnabled ? 'Override active' : 'Use custom query'}
            </button>
          </div>

          {metric.customQueryEnabled && <div className="mt-4" data-id="element-771" data-mp="8h3ua9curctw">
              <label className="block" data-id="element-772" data-mp="162rg60bomcr6">
                <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-amber-800" data-id="element-773" data-mp="161q0fijq1234">
                  Query definition
                </span>
                <textarea value={metric.customQueryText} onChange={event => onUpdate({
              customQueryText: event.target.value
            })} rows={7} spellCheck={false} placeholder="SELECT loan_officer, COUNT(*) FROM loan_files WHERE status = 'Funded' GROUP BY loan_officer" className="w-full resize-y rounded-lg border border-amber-200 bg-white px-3 py-2.5 font-mono text-xs leading-relaxed text-gray-800 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-amber-500" data-id="element-774" data-mp="wbmzmw6cyju8" />
              </label>
              <p className="mt-2 text-xs leading-snug text-amber-800" data-id="element-775" data-mp="zl6fnyks2m94">
                This query takes precedence over the visual builder when the definition is saved. Keep the visual fields as documentation for future administrators.
              </p>
              {!metric.customQueryText.trim() && <p className="mt-2 rounded-md border border-amber-300 bg-white/70 px-2.5 py-2 text-xs font-semibold text-amber-900" data-id="element-776" data-mp="1dkxshvzk5wlj">
                  Add a query before saving—an active override with no definition will not return any records.
                </p>}
            </div>}
        </div>

        {!metric.enabled && <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-600" data-id="element-777" data-mp="2b1wfmzylyhua">
            <CircleOff className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" data-id="element-778" data-mp="3vodyyc9qzw9f" />
            This metric is disabled and is hidden from the Point System
            reference until it is re-enabled.
          </div>}
      </div>
    </div>;
}
function FilterGroup({
  timeframeLabel,
  emptyState,
  rules,
  schema,
  relatedRecords,
  onAdd,
  onUpdate,
  onRemove
}: {
  timeframeLabel: string;
  emptyState: string;
  rules: MetricFilterRule[];
  schema: SourceObjectSchema;
  relatedRecords: RelatedRecordSchema[];
  onAdd: () => void;
  onUpdate: (index: number, changes: Partial<MetricFilterRule>) => void;
  onRemove: (index: number) => void;
}) {
  return <div className="rounded-xl border border-gray-200 bg-white p-4" data-id="element-779" data-mp="3hq68mn77vt78">
      <div className="mb-4 flex items-start justify-between gap-3" data-id="element-780" data-mp="111skkj14iyob">
        <div data-id="element-781" data-mp="1146gsv7jigxg">
          <h4 className="flex items-center gap-1.5 text-sm font-extrabold text-gray-800" data-id="element-782" data-mp="x10c3s02sxt3">
            <Filter className="h-4 w-4 text-blue-600" data-id="element-783" data-mp="3pv78cxg56nfc" />
            Conditions
          </h4>
          <p className="mt-0.5 text-xs leading-snug text-gray-500" data-id="element-784" data-mp="3pxl65e27idql">
            Every condition is combined with AND to build the query’s WHERE
            clause. Use a negative operator such as “Does not equal” to rule
            records out.
          </p>
        </div>
        <button type="button" onClick={onAdd} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-bold text-blue-700 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-785" data-mp="7oswr55cipk4">
          <Plus className="h-4 w-4" data-id="element-786" data-mp="3g3b22z3wjr01" />
          Add condition
        </button>
      </div>

      <div className="mb-3 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 font-mono text-[11px] font-semibold text-blue-900" data-id="element-787" data-mp="3ky8hcnrrywld">
        <span className="rounded bg-blue-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white" data-id="element-788" data-mp="2serqa9ln8w37">
          Where
        </span>
        {timeframeLabel || 'Date'} = (timeframe)
      </div>

      {rules.length === 0 ? <div className="rounded-lg border border-dashed border-gray-200 bg-white px-3 py-4 text-sm text-gray-500" data-id="element-789" data-mp="2fv4hfmplqvrq">
          {emptyState}
        </div> : <div className="space-y-3" data-id="element-790" data-mp="26vhjjaw3083f">
          {rules.map((rule, index) => {
        const isRelated = rule.scope === 'related';
        const relatedRecord = relatedRecords.find(record => record.key === rule.relatedRecord) ?? relatedRecords[0];
        const fields = isRelated ? relatedRecord?.fields ?? [] : schema.sourceFields;
        const field = fields.find(item => item.key === rule.field) ?? fields[0];
        const supportsValue = rule.operator !== 'is_true' && rule.operator !== 'is_false';
        return <div key={rule.id} className="rounded-lg border border-gray-200 bg-white p-3" data-id="element-791" data-mp="324jqscjpn399" data-mpi={index}>
                <div className="mb-2 flex items-center justify-between gap-3" data-id="element-792" data-mp="372l7iqtnyqhk">
                  <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-blue-700" data-id="element-793" data-mp="2t4ejs3ifi6vm">
                    AND · Condition {index + 1}
                  </span>
                  <button type="button" onClick={() => onRemove(index)} className="rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-300" aria-label={`Remove condition ${index + 1}`} data-id="element-794" data-mp="2je216gu6cns4">
                    <Trash2 className="h-4 w-4" data-id="element-795" data-mp="2efia620q4lsw" />
                  </button>
                </div>
                <div className={`grid gap-2 ${isRelated ? 'sm:grid-cols-[120px_minmax(0,0.8fr)_minmax(0,1fr)_150px_minmax(0,1fr)]' : 'sm:grid-cols-[120px_minmax(0,1fr)_150px_minmax(0,1fr)]'}`} data-id="element-796" data-mp="20fpowm7yuxsb">
                  <select value={rule.scope} onChange={event => {
              const scope = event.target.value as FilterScope;
              const nextRelatedRecord = relatedRecords[0];
              onUpdate(index, scope === 'related' ? {
                scope,
                relatedRecord: nextRelatedRecord?.key,
                field: nextRelatedRecord?.fields[0]?.key ?? ''
              } : {
                scope,
                relatedRecord: undefined,
                field: schema.sourceFields[0]?.key ?? ''
              });
            }} aria-label={`Condition ${index + 1} field scope`} className="rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm font-semibold text-gray-700 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-797" data-mp="1lubtklb2nudk">
                    <option value="source" data-id="element-798" data-mp="3avgq2vjlp6uo">Source field</option>
                    <option value="related" data-id="element-799" data-mp="36vpumej4q2jt">Related record</option>
                  </select>
                  {isRelated && <select value={relatedRecord?.key ?? ''} onChange={event => {
              const nextRelatedRecord = relatedRecords.find(record => record.key === event.target.value);
              onUpdate(index, {
                relatedRecord: nextRelatedRecord?.key,
                field: nextRelatedRecord?.fields[0]?.key ?? ''
              });
            }} aria-label={`Condition ${index + 1} related record`} className="min-w-0 rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm font-semibold text-gray-700 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-800" data-mp="31wve5j8mwq2v">
                      {relatedRecords.map(record => <option key={record.key} value={record.key} data-id="element-801" data-mp="17n6lqr5dyluu">
                          {record.label}
                        </option>)}
                    </select>}
                  <select value={rule.field} onChange={event => onUpdate(index, {
              field: event.target.value
            })} aria-label={`Condition ${index + 1} field`} className="min-w-0 rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm font-semibold text-gray-700 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-802" data-mp="f03rwcbnmzax">
                    {fields.map(item => <option key={item.key} value={item.key} data-id="element-803" data-mp="ukx32e3hnjkf">
                        {item.label}
                      </option>)}
                  </select>
                  <select value={rule.operator} onChange={event => onUpdate(index, {
              operator: event.target.value as typeof rule.operator
            })} aria-label={`Condition ${index + 1} operator`} className="rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm font-semibold text-gray-700 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-804" data-mp="18eeltwocos9e">
                    {FILTER_OPERATORS.map(operator => <option key={operator.value} value={operator.value} data-id="element-805" data-mp="1w1wh426tq8h4">
                        {operator.label}
                      </option>)}
                  </select>
                  {supportsValue ? <input value={rule.value} onChange={event => onUpdate(index, {
              value: event.target.value
            })} placeholder={field?.valueType === 'number' ? 'Value' : 'Filter value'} aria-label={`Condition ${index + 1} filter value`} className="min-w-0 rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm text-gray-700 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-806" data-mp="28kqp24ql8did" /> : <div className="flex items-center rounded-lg border border-dashed border-gray-200 bg-white px-2.5 text-xs font-semibold text-gray-400" data-id="element-807" data-mp="3688y7go5vhmw">
                      No value needed
                    </div>}
                </div>
              </div>;
      })}
        </div>}
    </div>;
}